/**
 * Receipt Printer Service using QZ Tray
 *
 * Supports thermal receipt printers (Rongta RP332 and compatible)
 * using ESC/POS commands for formatting.
 *
 * QZ Tray must be installed and running on the POS terminal:
 * https://qz.io/download/
 */

import { POSReceipt, POSRefundReceipt, TillCloseReport, BCOrderReceipt } from './storeAPI';

// QZ Tray types
declare global {
  interface Window {
    qz: any;
  }
}

// QZ Tray Demo Certificate for Red Bridge POS
// This certificate was generated on the POS terminal and allows QZ Tray to trust our app
const QZ_CERTIFICATE = `-----BEGIN CERTIFICATE-----
MIIECzCCAvOgAwIBAgIGAZwCK0nGMA0GCSqGSIb3DQEBCwUAMIGiMQswCQYDVQQG
EwJVUzELMAkGA1UECAwCTlkxEjAQBgNVBAcMCUNhbmFzdG90YTEbMBkGA1UECgwS
UVogSW5kdXN0cmllcywgTExDMRswGQYDVQQLDBJRWiBJbmR1c3RyaWVzLCBMTEMx
HDAaBgkqhkiG9w0BCQEWDXN1cHBvcnRAcXouaW8xGjAYBgNVBAMMEVFaIFRyYXkg
RGVtbyBDZXJ0MB4XDTI2MDEyNzAxMTUxN1oXDTQ2MDEyNzAxMTUxN1owgaIxCzAJ
BgNVBAYTAlVTMQswCQYDVQQIDAJOWTESMBAGA1UEBwwJQ2FuYXN0b3RhMRswGQYD
VQQKDBJRWiBJbmR1c3RyaWVzLCBMTEMxGzAZBgNVBAsMElFaIEluZHVzdHJpZXMs
IExMQzEcMBoGCSqGSIb3DQEJARYNc3VwcG9ydEBxei5pbzEaMBgGA1UEAwwRUVog
VHJheSBEZW1vIENlcnQwggEiMA0GCSqGSIb3DQEBAQUAA4IBDwAwggEKAoIBAQCZ
dj8lOsawe58IDKJmGJ69dh/mnPDcVgdlKADeAcNkO/VNjAlBfuAPLibo+ooXJ8Tj
c1i+DqzplQLTSSseY6s8lGAmHSmpC6/9yawsKS2KSqA/Xy7E0K7Mc9ezxrpwZr3+
L9ajOHk9julC7Gq7oV5+r20eqEHfDPsHP7xBK9DR4BZNve6XPXd1U5od8dId9Qwo
SF6xIm882iOc79FsjzXV0kohW9XwhTLpgFY2FNw0QZsSp4ByQ6xudjApLvdpCNTk
IT6dwiLwrCx3K2sd7yBLozOqfu9J+liYLqZ/Z/VD2uhrzg6IAg2kyLZJed9vlEYM
TJYVGnyHW4tMm14IZz99AgMBAAGjRTBDMBIGA1UdEwEB/wQIMAYBAf8CAQEwDgYD
VR0PAQH/BAQDAgEGMB0GA1UdDgQWBBQfoq1zy8hyemQ3NaMy1zPgbyBpNTANBgkq
hkiG9w0BAQsFAAOCAQEAISH0gEKoAwdn0L2SZVXVajxnqf62mb8R3Y/J69KRy1HC
ZYMNMVIeSmrWbrnoWx2iLec15FQQ07Twc4Z7QEKIhXn39Bk30GqtPdqxIokZCJN/
axL0dyyAEKpt7d5t1id3umKR9bitkFjkbQZ5rKfihSKa8xt4WqzTa3/0VB/yQ+2e
jilWGiswn5lperJrh5XF0uglZ9BBzcXuWxl3dd8lYgF/JYeD5Rs8/6Xm1QkF1jed
qPGx3KqcyjtKKjyANQ819DNN773ka63jnkyEe7T7QY6MWLTGS8hr1DH9X5ifS6TH
ypgF9djeKiNWoVLpi8R24Dzt/Q7m3AzMfrgUuHymDA==
-----END CERTIFICATE-----`;

// Initialize QZ Tray security when this module loads
const initQzSecurity = () => {
  if (typeof window !== 'undefined' && window.qz) {
    // Set certificate promise
    window.qz.security.setCertificatePromise((resolve: (cert: string) => void) => {
      resolve(QZ_CERTIFICATE);
    });

    // Demo certs don't require signing - return empty signature
    // QZ Tray will accept this from the machine that created the demo cert
    window.qz.security.setSignaturePromise(() => {
      return (resolve: (sig: string) => void) => {
        resolve('');
      };
    });
  }
};

// Try to init security immediately and also when window loads
if (typeof window !== 'undefined') {
  if (window.qz) {
    initQzSecurity();
  } else {
    // QZ Tray script may not be loaded yet, wait for it
    window.addEventListener('load', () => {
      setTimeout(initQzSecurity, 500);
    });
  }
}

// ESC/POS Constants
const ESC = '\x1B';
const GS = '\x1D';
const LF = '\n';

// Store Information - Red Bridge Farm Feed Co.
const STORE_INFO = {
  name1: 'RED BRIDGE FARM',
  name2: 'FEED CO.',
  address: '145 W 3rd Ave',
  cityState: 'Kettle Falls, WA 99141',
  phone: '(509) 738-9004',
  tagline: '"Quality Feed for Quality Animals"',
  bridgeClubUrl: 'https://bridgeclub.redbridgefeed.com',
};

// ESC/POS Commands
const COMMANDS = {
  // Initialize printer
  INIT: ESC + '@',

  // Text alignment
  ALIGN_LEFT: ESC + 'a' + '\x00',
  ALIGN_CENTER: ESC + 'a' + '\x01',
  ALIGN_RIGHT: ESC + 'a' + '\x02',

  // Text formatting
  BOLD_ON: ESC + 'E' + '\x01',
  BOLD_OFF: ESC + 'E' + '\x00',
  DOUBLE_HEIGHT_ON: GS + '!' + '\x10',
  DOUBLE_WIDTH_ON: GS + '!' + '\x20',
  DOUBLE_SIZE_ON: GS + '!' + '\x30',
  NORMAL_SIZE: GS + '!' + '\x00',
  UNDERLINE_ON: ESC + '-' + '\x01',
  UNDERLINE_OFF: ESC + '-' + '\x00',

  // Line spacing
  LINE_SPACING_DEFAULT: ESC + '2',
  LINE_SPACING_SET: ESC + '3',

  // Paper handling
  CUT_PARTIAL: GS + 'V' + '\x01',
  CUT_FULL: GS + 'V' + '\x00',
  FEED_LINES: (n: number) => ESC + 'd' + String.fromCharCode(n),

  // Cash drawer
  OPEN_DRAWER: ESC + 'p' + '\x00' + '\x19' + '\xFA',

  // QR Code commands (for ESC/POS printers with QR support)
  QR_MODEL: GS + '(k' + '\x04\x00' + '1A2\x00',  // Model 2
  QR_SIZE: (n: number) => GS + '(k' + '\x03\x00' + '1C' + String.fromCharCode(n),  // Size 1-16
  QR_ERROR: GS + '(k' + '\x03\x00' + '1E1',  // Error correction L
  QR_STORE: (data: string) => {
    const len = data.length + 3;
    const pL = String.fromCharCode(len % 256);
    const pH = String.fromCharCode(Math.floor(len / 256));
    return GS + '(k' + pL + pH + '1P0' + data;
  },
  QR_PRINT: GS + '(k' + '\x03\x00' + '1Q0',
};

// Paper width in characters (for 80mm paper)
const PAPER_WIDTH = 48;

// Word-wrap text to the given width WITHOUT chopping words — used for
// invoice line-item names in the INVPAY receipt block so a long feed
// name wraps to a second line instead of truncating mid-word. A single
// word longer than the width is hard-split as a last resort so it can
// never overflow the paper. Exported for tests.
export const wrapReceiptText = (text: string, width: number): string[] => {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const wrapped: string[] = [];
  let current = '';
  for (const word of words) {
    if (!current) {
      current = word;
    } else if (current.length + 1 + word.length <= width) {
      current += ' ' + word;
    } else {
      wrapped.push(current);
      current = word;
    }
  }
  if (current) wrapped.push(current);
  const out: string[] = [];
  for (const line of wrapped) {
    let rest = line;
    while (rest.length > width) {
      out.push(rest.slice(0, width));
      rest = rest.slice(width);
    }
    out.push(rest);
  }
  return out;
};

class ReceiptPrinterService {
  private qz: any = null;
  private printerName: string = '';
  private connected: boolean = false;

  /**
   * Initialize QZ Tray connection
   */
  async connect(): Promise<boolean> {
    if (this.connected) return true;

    try {
      // Load QZ Tray if not already loaded
      if (!window.qz) {
        console.error('QZ Tray not loaded. Please install QZ Tray.');
        return false;
      }

      this.qz = window.qz;

      // Check if already connected
      if (this.qz.websocket.isActive()) {
        this.connected = true;
        return true;
      }

      // Connect to QZ Tray
      await this.qz.websocket.connect();
      this.connected = true;

      console.log('Connected to QZ Tray');
      return true;
    } catch (err) {
      console.error('Failed to connect to QZ Tray:', err);
      return false;
    }
  }

  /**
   * Disconnect from QZ Tray
   */
  async disconnect(): Promise<void> {
    if (this.qz && this.connected) {
      try {
        await this.qz.websocket.disconnect();
      } catch (err) {
        console.error('Error disconnecting from QZ Tray:', err);
      }
    }
    this.connected = false;
  }

  /**
   * Find available receipt printers
   */
  async findPrinters(): Promise<string[]> {
    if (!await this.connect()) return [];

    try {
      const printers = await this.qz.printers.find();
      // Filter for likely receipt printers
      const receiptPrinters = printers.filter((p: string) =>
        p.toLowerCase().includes('receipt') ||
        p.toLowerCase().includes('pos') ||
        p.toLowerCase().includes('thermal') ||
        p.toLowerCase().includes('rongta') ||
        p.toLowerCase().includes('epson') ||
        p.toLowerCase().includes('star')
      );

      return receiptPrinters.length > 0 ? receiptPrinters : printers;
    } catch (err) {
      console.error('Failed to find printers:', err);
      return [];
    }
  }

  /**
   * Set the printer to use
   */
  setPrinter(name: string): void {
    this.printerName = name;
  }

  /**
   * Print a receipt
   */
  async printReceipt(receipt: POSReceipt, openDrawer: boolean = false): Promise<boolean> {
    if (!await this.connect()) {
      throw new Error('Could not connect to QZ Tray. Please ensure QZ Tray is installed and running.');
    }

    if (!this.printerName) {
      // Try to find a receipt printer
      const printers = await this.findPrinters();
      if (printers.length === 0) {
        throw new Error('No receipt printer found. Please configure a printer.');
      }
      this.printerName = printers[0];
    }

    try {
      const data = this.buildReceiptData(receipt, openDrawer);
      const config = this.qz.configs.create(this.printerName);

      await this.qz.print(config, data);
      console.log('Receipt printed successfully');
      return true;
    } catch (err) {
      console.error('Failed to print receipt:', err);
      throw err;
    }
  }

  /**
   * Open cash drawer
   */
  async openDrawer(): Promise<boolean> {
    if (!await this.connect()) return false;

    if (!this.printerName) {
      const printers = await this.findPrinters();
      if (printers.length === 0) return false;
      this.printerName = printers[0];
    }

    try {
      const config = this.qz.configs.create(this.printerName);
      await this.qz.print(config, [{ type: 'raw', format: 'command', data: COMMANDS.OPEN_DRAWER }]);
      return true;
    } catch (err) {
      console.error('Failed to open drawer:', err);
      return false;
    }
  }

  /**
   * Build ESC/POS data for a receipt
   */
  private buildReceiptData(receipt: POSReceipt, openDrawer: boolean): any[] {
    const lines: string[] = [];

    // Initialize
    lines.push(COMMANDS.INIT);

    // Store header - centered, bold, double height (not double width to avoid wrapping)
    lines.push(COMMANDS.ALIGN_CENTER);
    lines.push(COMMANDS.DOUBLE_HEIGHT_ON);
    lines.push(COMMANDS.BOLD_ON);
    lines.push(STORE_INFO.name1 + LF);
    lines.push(STORE_INFO.name2 + LF);
    lines.push(COMMANDS.NORMAL_SIZE);
    lines.push(COMMANDS.BOLD_OFF);

    // Store address info
    lines.push(STORE_INFO.address + LF);
    lines.push(STORE_INFO.cityState + LF);
    lines.push(STORE_INFO.phone + LF);
    lines.push(LF);

    // Transaction info
    lines.push(this.dividerLine('='));
    lines.push(`Date: ${receipt.date}  Time: ${receipt.time}` + LF);
    lines.push(`Transaction: ${receipt.transaction_number}` + LF);
    lines.push(`Cashier: ${receipt.cashier}` + LF);
    if (receipt.customer_name) {
      lines.push(`Customer: ${receipt.customer_name}` + LF);
    }
    lines.push(this.dividerLine('='));
    lines.push(LF);

    // Items - left aligned
    lines.push(COMMANDS.ALIGN_LEFT);

    for (const item of receipt.items) {
      // Invoice-payment lines (A1): dedicated block so the invoice
      // number NEVER truncates ("Invoice Payment — INV-YYYYMMDD-NNN"
      // is longer than the generic name budget). No qty line — "1 x"
      // is noise for a balance payment — and no TAX EXEMPT label
      // (debt collection isn't a taxable sale to begin with).
      if (item.invoice_payment) {
        const inv = item.invoice_payment;
        const price = `$${item.total.toFixed(2)}`;
        lines.push(COMMANDS.BOLD_ON);
        lines.push(this.padRight('INVOICE PAYMENT', PAPER_WIDTH - price.length) + price + LF);
        lines.push(COMMANDS.BOLD_OFF);
        lines.push(`  Invoice ${inv.invoice_number}` + LF);
        // Invoice contents — what the invoice was FOR. Read-only
        // context: the amount charged is the applied payment above,
        // not these line totals. Names word-wrap, never truncate.
        for (const li of inv.line_items || []) {
          for (const nameLine of wrapReceiptText(li.name, PAPER_WIDTH - 2)) {
            lines.push('  ' + nameLine + LF);
          }
          const qtyParts: string[] = [];
          if (li.quantity_lbs > 0) qtyParts.push(`${li.quantity_lbs} lbs`);
          if (li.packages > 0) qtyParts.push(`${li.packages} ${li.packages === 1 ? 'bag' : 'bags'}`);
          const detail = '    ' + (qtyParts.join(' / ') || 'line total');
          const amt = `$${li.line_total.toFixed(2)}`;
          lines.push(this.padRight(detail, PAPER_WIDTH - amt.length) + amt + LF);
        }
        if (inv.paid_in_full === true) {
          lines.push(COMMANDS.BOLD_ON);
          lines.push('  ** PAID IN FULL **' + LF);
          lines.push(COMMANDS.BOLD_OFF);
        } else if (inv.remaining_balance !== null && inv.remaining_balance !== undefined) {
          const bal = `$${inv.remaining_balance.toFixed(2)}`;
          lines.push(this.padRight('  Remaining balance:', PAPER_WIDTH - bal.length) + bal + LF);
        }
        continue;
      }

      // Item name (with TAX EXEMPT label if applicable). weight_tracked lines carry
      // unit='lb', so the quantity-detail line below already prints
      // "0.58 lb @ $20.00/lb" — no weight in the name needed.
      const taxExLabel = item.is_tax_exempt ? ' [TAX EXEMPT]' : '';
      const name = this.truncate(item.name, PAPER_WIDTH - 10 - taxExLabel.length) + taxExLabel;
      const price = `$${item.total.toFixed(2)}`;

      // First line: name and price
      lines.push(this.padRight(name, PAPER_WIDTH - price.length) + price + LF);

      // Second line: quantity details (indented)
      const qtyDetail = item.unit === 'lb'
        ? `  ${item.quantity.toFixed(2)} lb @ $${item.unit_price.toFixed(2)}/lb`
        : `  ${item.quantity} x $${item.unit_price.toFixed(2)}`;
      lines.push(qtyDetail + LF);

      // Discount line (if item has a discount)
      if (item.discount_amount && item.discount_amount > 0) {
        const discountDesc = item.discount_type === 'percent'
          ? `  Discount (${item.discount_value}% off)`
          : `  Discount ($${(item.discount_value || 0).toFixed(2)} off)`;
        const discountAmt = `-$${item.discount_amount.toFixed(2)}`;
        lines.push(this.padRight(discountDesc, PAPER_WIDTH - discountAmt.length) + discountAmt + LF);
      }

      // Bag-credit pull line (Phase 3): show it was applied from credit +
      // remaining, matching the on-screen receipt. Word-wraps, never truncates.
      if (item.is_donation && item.bag_credit_label) {
        for (const labelLine of wrapReceiptText(item.bag_credit_label, PAPER_WIDTH - 2)) {
          lines.push('  ' + labelLine + LF);
        }
      }
    }

    lines.push(LF);
    lines.push(this.dividerLine('-'));

    // Totals - right aligned for amounts
    lines.push(COMMANDS.ALIGN_LEFT);
    lines.push(this.formatTotalLine('Subtotal:', receipt.subtotal));
    lines.push(this.formatTotalLine('Tax:', receipt.tax));

    if (receipt.discount > 0) {
      let discountLabel = 'Discount:';
      if (receipt.cart_discount_type) {
        discountLabel = receipt.cart_discount_type === 'percent'
          ? `Discount (${receipt.cart_discount_value}% off):`
          : `Discount ($${(receipt.cart_discount_value || 0).toFixed(2)} off):`;
      }
      lines.push(this.formatTotalLine(discountLabel, -receipt.discount));
    }

    if (receipt.surcharge > 0) {
      lines.push(this.formatTotalLine('Credit Card Fee (3%):', receipt.surcharge));
    }

    // Tax Exempt notice
    if (receipt.tax_exempt) {
      lines.push(LF);
      lines.push(COMMANDS.BOLD_ON);
      lines.push('Tax Exempt - Farmer Certificate on File' + LF);
      lines.push(COMMANDS.BOLD_OFF);
    }

    lines.push(this.dividerLine('-'));

    // Grand total - bold, larger
    lines.push(COMMANDS.BOLD_ON);
    lines.push(COMMANDS.DOUBLE_HEIGHT_ON);
    lines.push(this.formatTotalLine('TOTAL:', receipt.total));
    lines.push(COMMANDS.NORMAL_SIZE);
    lines.push(COMMANDS.BOLD_OFF);
    lines.push(LF);

    // Payment info
    lines.push(COMMANDS.BOLD_ON);
    lines.push('PAYMENT' + LF);
    lines.push(COMMANDS.BOLD_OFF);

    for (const payment of receipt.payments) {
      let paymentLine = `${payment.method}: $${payment.amount.toFixed(2)}`;
      if (payment.card_type && payment.card_last_four) {
        paymentLine += ` (${payment.card_type} ****${payment.card_last_four})`;
      }
      if ((payment as any).check_number) {
        paymentLine += ` (Check #${(payment as any).check_number})`;
      }
      lines.push(paymentLine + LF);

      if (payment.change && payment.change > 0) {
        lines.push(`Change: $${payment.change.toFixed(2)}` + LF);
      }
    }

    lines.push(LF);

    // Footer - Thank you section
    lines.push(COMMANDS.ALIGN_CENTER);
    lines.push(this.dividerLine('='));
    lines.push(LF);
    lines.push('Thank you for your purchase!' + LF);
    lines.push(`${STORE_INFO.name1} ${STORE_INFO.name2}` + LF);
    lines.push(STORE_INFO.tagline + LF);
    lines.push(LF);

    // Bridge Club promotion with QR code
    lines.push(this.dividerLine('-'));
    lines.push(COMMANDS.BOLD_ON);
    lines.push('JOIN THE BRIDGE CLUB!' + LF);
    lines.push(COMMANDS.BOLD_OFF);
    lines.push('Earn rewards on every purchase' + LF);
    lines.push(LF);

    // QR Code for Bridge Club
    lines.push(COMMANDS.QR_MODEL);
    lines.push(COMMANDS.QR_SIZE(5));
    lines.push(COMMANDS.QR_ERROR);
    lines.push(COMMANDS.QR_STORE(STORE_INFO.bridgeClubUrl));
    lines.push(COMMANDS.QR_PRINT);
    lines.push(LF);

    lines.push('Scan to join or visit:' + LF);
    lines.push('bridgeclub.redbridgefeed.com' + LF);
    lines.push(this.dividerLine('-'));
    lines.push(LF);

    // Feed paper and cut
    lines.push(COMMANDS.FEED_LINES(3));
    lines.push(COMMANDS.CUT_PARTIAL);

    // Open drawer if requested
    if (openDrawer) {
      lines.push(COMMANDS.OPEN_DRAWER);
    }

    // Return as raw print data
    return [{
      type: 'raw',
      format: 'plain',
      data: lines.join(''),
    }];
  }

  /**
   * Print a refund receipt
   */
  async printRefundReceipt(receipt: POSRefundReceipt, openDrawer: boolean = false): Promise<boolean> {
    if (!await this.connect()) {
      throw new Error('Could not connect to QZ Tray. Please ensure QZ Tray is installed and running.');
    }

    if (!this.printerName) {
      const printers = await this.findPrinters();
      if (printers.length === 0) {
        throw new Error('No receipt printer found. Please configure a printer.');
      }
      this.printerName = printers[0];
    }

    try {
      const data = this.buildRefundReceiptData(receipt, openDrawer);
      const config = this.qz.configs.create(this.printerName);
      await this.qz.print(config, data);
      console.log('Refund receipt printed successfully');
      return true;
    } catch (err) {
      console.error('Failed to print refund receipt:', err);
      throw err;
    }
  }

  /**
   * Build ESC/POS data for a refund receipt
   */
  private buildRefundReceiptData(receipt: POSRefundReceipt, openDrawer: boolean): any[] {
    const lines: string[] = [];

    // Initialize
    lines.push(COMMANDS.INIT);

    // Store header
    lines.push(COMMANDS.ALIGN_CENTER);
    lines.push(COMMANDS.DOUBLE_HEIGHT_ON);
    lines.push(COMMANDS.BOLD_ON);
    lines.push(STORE_INFO.name1 + LF);
    lines.push(STORE_INFO.name2 + LF);
    lines.push(COMMANDS.NORMAL_SIZE);
    lines.push(COMMANDS.BOLD_OFF);

    lines.push(STORE_INFO.address + LF);
    lines.push(STORE_INFO.cityState + LF);
    lines.push(STORE_INFO.phone + LF);
    lines.push(LF);

    // REFUND banner
    lines.push(COMMANDS.BOLD_ON);
    lines.push(COMMANDS.DOUBLE_SIZE_ON);
    lines.push('*** REFUND ***' + LF);
    lines.push(COMMANDS.NORMAL_SIZE);
    lines.push(COMMANDS.BOLD_OFF);
    lines.push(LF);

    // Refund info
    lines.push(this.dividerLine('='));
    lines.push(`Date: ${receipt.date}  Time: ${receipt.time}` + LF);
    lines.push(`Refund #: ${receipt.refund_number}` + LF);
    lines.push(`Original Txn: ${receipt.original_transaction_number}` + LF);
    lines.push(`Cashier: ${receipt.cashier}` + LF);
    lines.push(this.dividerLine('='));
    lines.push(LF);

    // Items
    lines.push(COMMANDS.ALIGN_LEFT);

    for (const item of receipt.items) {
      const name = this.truncate(item.name, PAPER_WIDTH - 10);
      const price = `$${item.total.toFixed(2)}`;
      lines.push(this.padRight(name, PAPER_WIDTH - price.length) + price + LF);

      const qtyDetail = item.unit === 'lb'
        ? `  ${item.quantity.toFixed(2)} lb @ $${item.unit_price.toFixed(2)}/lb`
        : `  ${item.quantity} x $${item.unit_price.toFixed(2)}`;
      lines.push(qtyDetail + LF);
    }

    lines.push(LF);
    lines.push(this.dividerLine('-'));

    // Totals
    lines.push(COMMANDS.ALIGN_LEFT);
    lines.push(this.formatTotalLine('Refund Subtotal:', receipt.subtotal));
    lines.push(this.formatTotalLine('Tax Refund:', receipt.tax));

    if (receipt.surcharge > 0) {
      lines.push(this.formatTotalLine('Surcharge Refund:', receipt.surcharge));
    }

    lines.push(this.dividerLine('-'));

    // Grand total
    lines.push(COMMANDS.BOLD_ON);
    lines.push(COMMANDS.DOUBLE_HEIGHT_ON);
    lines.push(this.formatTotalLine('REFUND TOTAL:', receipt.total));
    lines.push(COMMANDS.NORMAL_SIZE);
    lines.push(COMMANDS.BOLD_OFF);
    lines.push(LF);

    // Payment method
    lines.push(COMMANDS.BOLD_ON);
    lines.push('RETURNED VIA' + LF);
    lines.push(COMMANDS.BOLD_OFF);

    if (receipt.payment_method === 'cash') {
      lines.push(`Cash: $${receipt.total.toFixed(2)}` + LF);
    } else {
      const cardLabel = receipt.card_type && receipt.card_last_four
        ? `${receipt.card_type} ****${receipt.card_last_four}`
        : 'Card';
      lines.push(`${cardLabel}: $${receipt.total.toFixed(2)}` + LF);
    }

    lines.push(LF);

    // Reason
    lines.push(COMMANDS.ALIGN_LEFT);
    lines.push(`Reason: ${receipt.reason}` + LF);
    lines.push(LF);

    // Footer
    lines.push(COMMANDS.ALIGN_CENTER);
    lines.push(this.dividerLine('='));
    lines.push(LF);
    lines.push(`${STORE_INFO.name1} ${STORE_INFO.name2}` + LF);
    lines.push(STORE_INFO.tagline + LF);
    lines.push(LF);

    // Feed and cut
    lines.push(COMMANDS.FEED_LINES(3));
    lines.push(COMMANDS.CUT_PARTIAL);

    // Open drawer for cash refunds
    if (openDrawer) {
      lines.push(COMMANDS.OPEN_DRAWER);
    }

    return [{
      type: 'raw',
      format: 'plain',
      data: lines.join(''),
    }];
  }

  /**
   * Print a till close report
   */
  async printTillReport(data: TillCloseReport): Promise<boolean> {
    if (!await this.connect()) {
      throw new Error('Could not connect to QZ Tray. Please ensure QZ Tray is installed and running.');
    }

    if (!this.printerName) {
      const printers = await this.findPrinters();
      if (printers.length === 0) {
        throw new Error('No receipt printer found. Please configure a printer.');
      }
      this.printerName = printers[0];
    }

    try {
      const printData = this.buildTillReportData(data);
      const config = this.qz.configs.create(this.printerName);
      await this.qz.print(config, printData);
      console.log('Till report printed successfully');
      return true;
    } catch (err) {
      console.error('Failed to print till report:', err);
      throw err;
    }
  }

  /**
   * Build ESC/POS data for a till close report
   */
  private buildTillReportData(data: TillCloseReport): any[] {
    const lines: string[] = [];

    // Initialize
    lines.push(COMMANDS.INIT);

    // Store header
    lines.push(COMMANDS.ALIGN_CENTER);
    lines.push(COMMANDS.DOUBLE_HEIGHT_ON);
    lines.push(COMMANDS.BOLD_ON);
    lines.push(STORE_INFO.name1 + LF);
    lines.push(STORE_INFO.name2 + LF);
    lines.push(COMMANDS.NORMAL_SIZE);
    lines.push(COMMANDS.BOLD_OFF);

    lines.push(STORE_INFO.address + LF);
    lines.push(STORE_INFO.cityState + LF);
    lines.push(STORE_INFO.phone + LF);
    lines.push(LF);

    // TILL CLOSE REPORT banner
    lines.push(COMMANDS.BOLD_ON);
    lines.push(COMMANDS.DOUBLE_SIZE_ON);
    lines.push('TILL CLOSE REPORT' + LF);
    lines.push(COMMANDS.NORMAL_SIZE);
    lines.push(COMMANDS.BOLD_OFF);
    lines.push(LF);

    // Session info
    lines.push(this.dividerLine('='));
    lines.push(COMMANDS.ALIGN_LEFT);
    lines.push(`Cashier: ${data.session.cashier}` + LF);
    lines.push(`Terminal: ${data.session.terminal_id}` + LF);
    const openTime = new Date(data.session.opened_at);
    lines.push(`Shift Start: ${openTime.toLocaleDateString()} ${openTime.toLocaleTimeString()}` + LF);
    if (data.session.closed_at) {
      const closeTime = new Date(data.session.closed_at);
      lines.push(`Shift End:   ${closeTime.toLocaleDateString()} ${closeTime.toLocaleTimeString()}` + LF);
    }
    lines.push(this.dividerLine('='));
    lines.push(LF);

    // Sales Summary
    lines.push(COMMANDS.BOLD_ON);
    lines.push('SALES SUMMARY' + LF);
    lines.push(COMMANDS.BOLD_OFF);
    lines.push(this.dividerLine('-'));
    lines.push(this.formatTotalLine(`Transactions: ${data.sales_summary.transaction_count}`, data.sales_summary.subtotal));
    lines.push(this.formatTotalLine('Tax:', data.sales_summary.tax));
    if (data.sales_summary.discounts > 0) {
      lines.push(this.formatTotalLine('Discounts:', -data.sales_summary.discounts));
    }
    if (data.sales_summary.surcharges > 0) {
      lines.push(this.formatTotalLine('Surcharges:', data.sales_summary.surcharges));
    }
    lines.push(this.dividerLine('-'));
    lines.push(COMMANDS.BOLD_ON);
    lines.push(this.formatTotalLine('TOTAL SALES:', data.sales_summary.total));
    lines.push(COMMANDS.BOLD_OFF);
    if (data.sales_summary.voided_count > 0) {
      lines.push(`Voids: ${data.sales_summary.voided_count}` + LF);
    }
    if (data.sales_summary.refund_count > 0) {
      lines.push(this.formatTotalLine(`Refunds (${data.sales_summary.refund_count}):`, -data.sales_summary.refund_total));
    }
    lines.push(LF);

    // Payment Breakdown
    lines.push(COMMANDS.BOLD_ON);
    lines.push('PAYMENT BREAKDOWN' + LF);
    lines.push(COMMANDS.BOLD_OFF);
    lines.push(this.dividerLine('-'));
    lines.push(this.formatTotalLine('Cash:', data.payment_breakdown.cash));
    lines.push(this.formatTotalLine('Credit:', data.payment_breakdown.credit));
    lines.push(this.formatTotalLine('Debit:', data.payment_breakdown.debit));
    if (data.payment_breakdown.check > 0) {
      lines.push(this.formatTotalLine('Check:', data.payment_breakdown.check));
    }
    if ((data.payment_breakdown.account ?? 0) > 0) {
      lines.push(this.formatTotalLine('On Account:', data.payment_breakdown.account ?? 0));
    }
    // Memo: settlement of an existing tab — its money is already in the tenders
    // above (cash/credit/debit/check), NOT an extra sale. Not summed in.
    if ((data.tab_paydowns ?? 0) > 0) {
      lines.push(this.formatTotalLine('Tab Pay-downs (memo):', data.tab_paydowns ?? 0));
    }
    lines.push(LF);

    // Cash Reconciliation
    lines.push(COMMANDS.BOLD_ON);
    lines.push('CASH RECONCILIATION' + LF);
    lines.push(COMMANDS.BOLD_OFF);
    lines.push(this.dividerLine('-'));
    lines.push(this.formatTotalLine('Opening Cash:', data.cash_reconciliation.opening));
    lines.push(this.formatTotalLine('+ Cash Sales:', data.cash_reconciliation.cash_sales));
    lines.push(this.formatTotalLine('- Change Given:', -data.cash_reconciliation.change_given));
    if (data.cash_reconciliation.cash_refunds > 0) {
      lines.push(this.formatTotalLine('- Cash Refunds:', -data.cash_reconciliation.cash_refunds));
    }
    if (data.cash_reconciliation.petty_cash_out > 0) {
      lines.push(this.formatTotalLine('- Petty Cash:', -data.cash_reconciliation.petty_cash_out));
    }
    lines.push(this.dividerLine('-'));
    lines.push(COMMANDS.BOLD_ON);
    lines.push(this.formatTotalLine('EXPECTED:', data.cash_reconciliation.expected));
    lines.push(COMMANDS.BOLD_OFF);
    if (data.cash_reconciliation.closing != null) {
      lines.push(this.formatTotalLine('Counted:', data.cash_reconciliation.closing));
      if (data.cash_reconciliation.variance != null) {
        lines.push(COMMANDS.BOLD_ON);
        lines.push(this.formatTotalLine('VARIANCE:', data.cash_reconciliation.variance));
        lines.push(COMMANDS.BOLD_OFF);
      }
    }
    lines.push(LF);

    // Petty Cash Withdrawals detail
    if (data.petty_cash_withdrawals.length > 0) {
      lines.push(COMMANDS.BOLD_ON);
      lines.push('PETTY CASH WITHDRAWALS' + LF);
      lines.push(COMMANDS.BOLD_OFF);
      lines.push(this.dividerLine('-'));
      let pettyTotal = 0;
      for (const w of data.petty_cash_withdrawals) {
        const amt = `$${w.amount.toFixed(2)}`;
        lines.push(this.padRight(`${w.time} ${w.category}`, PAPER_WIDTH - amt.length) + amt + LF);
        if (w.description) {
          lines.push(`  ${this.truncate(w.description, PAPER_WIDTH - 2)}` + LF);
        }
        pettyTotal += w.amount;
      }
      lines.push(this.dividerLine('-'));
      lines.push(COMMANDS.BOLD_ON);
      lines.push(this.formatTotalLine('TOTAL PETTY CASH:', pettyTotal));
      lines.push(COMMANDS.BOLD_OFF);
      lines.push(LF);
    }

    // Tax Summary
    lines.push(COMMANDS.BOLD_ON);
    lines.push('TAX SUMMARY' + LF);
    lines.push(COMMANDS.BOLD_OFF);
    lines.push(this.dividerLine('-'));
    lines.push(this.formatTotalLine('Taxable Sales:', data.tax_summary.taxable_sales));
    lines.push(this.formatTotalLine('Exempt Sales:', data.tax_summary.exempt_sales));
    lines.push(this.formatTotalLine('Tax Collected:', data.tax_summary.tax_collected));
    lines.push(LF);

    // Footer
    lines.push(COMMANDS.ALIGN_CENTER);
    lines.push(this.dividerLine('='));
    const now = new Date();
    lines.push(`Printed: ${now.toLocaleDateString()} ${now.toLocaleTimeString()}` + LF);
    lines.push(LF);

    // Feed and cut
    lines.push(COMMANDS.FEED_LINES(3));
    lines.push(COMMANDS.CUT_PARTIAL);

    return [{
      type: 'raw',
      format: 'plain',
      data: lines.join(''),
    }];
  }

  /**
   * Print a Bridge Club order receipt (paid-online order, no till involved).
   *
   * Same connect / printer-selection contract as printReceipt: throws a
   * readable Error when QZ Tray is unreachable or no printer is configured,
   * so the caller can surface the message rather than a raw exception.
   */
  async printBCOrderReceipt(receipt: BCOrderReceipt): Promise<boolean> {
    if (!await this.connect()) {
      throw new Error('Could not connect to QZ Tray. Please ensure QZ Tray is installed and running.');
    }

    if (!this.printerName) {
      const printers = await this.findPrinters();
      if (printers.length === 0) {
        throw new Error('No receipt printer found. Please configure a printer.');
      }
      this.printerName = printers[0];
    }

    try {
      const data = this.buildBCOrderReceiptData(receipt);
      const config = this.qz.configs.create(this.printerName);

      await this.qz.print(config, data);
      console.log('BC order receipt printed successfully');
      return true;
    } catch (err) {
      console.error('Failed to print BC order receipt:', err);
      throw err;
    }
  }

  /**
   * Build ESC/POS data for a Bridge Club order receipt.
   *
   * Mirrors buildReceiptData's layout so the two receipts look like the same
   * store. Differences are forced by the data, not by choice:
   *   - no Transaction/Cashier lines (a BC order has neither) — the order
   *     number takes the transaction slot
   *   - a FULFILLMENT block the POS receipt has no equivalent for
   *   - no per-line tax, no discount lines, no cash-drawer kick
   *   - no points line: paid-online BC orders accrue nothing (see
   *     store_order_receipt), so printing a figure would be a lie
   */
  private buildBCOrderReceiptData(receipt: BCOrderReceipt): any[] {
    const lines: string[] = [];

    lines.push(COMMANDS.INIT);

    // Store header — identical to the POS receipt.
    lines.push(COMMANDS.ALIGN_CENTER);
    lines.push(COMMANDS.DOUBLE_HEIGHT_ON);
    lines.push(COMMANDS.BOLD_ON);
    lines.push(STORE_INFO.name1 + LF);
    lines.push(STORE_INFO.name2 + LF);
    lines.push(COMMANDS.NORMAL_SIZE);
    lines.push(COMMANDS.BOLD_OFF);

    lines.push(STORE_INFO.address + LF);
    lines.push(STORE_INFO.cityState + LF);
    lines.push(STORE_INFO.phone + LF);
    lines.push(LF);

    // Fulfillment banner. The route word is keyed off the backend's
    // pickup_method enum; the locker NUMBER (and the "(unassigned)" wording)
    // is the backend's fulfillment_label printed verbatim. No locker logic
    // exists on this side, so the receipt can never disagree with the
    // orders-history row.
    const isLocker = receipt.pickup_method === 'locker';
    lines.push(COMMANDS.BOLD_ON);
    lines.push(COMMANDS.DOUBLE_HEIGHT_ON);
    lines.push((isLocker ? 'LOCKER PICKUP' : 'IN-STORE PICKUP') + LF);
    lines.push(COMMANDS.NORMAL_SIZE);
    if (isLocker && receipt.fulfillment_label) {
      lines.push(receipt.fulfillment_label + LF);
    }
    lines.push(COMMANDS.BOLD_OFF);
    lines.push(LF);

    // Order info
    lines.push(this.dividerLine('='));
    lines.push(`Date: ${receipt.date}  Time: ${receipt.time}` + LF);
    lines.push(`Order: ${receipt.order_number}` + LF);
    if (receipt.customer_name) {
      lines.push(`Customer: ${receipt.customer_name}` + LF);
    }
    lines.push(this.dividerLine('='));
    lines.push(LF);

    // Items
    lines.push(COMMANDS.ALIGN_LEFT);

    for (const item of receipt.items) {
      const price = `$${item.line_total.toFixed(2)}`;
      const name = this.truncate(item.name, PAPER_WIDTH - 10);
      lines.push(this.padRight(name, PAPER_WIDTH - price.length) + price + LF);
      // BC lines are bagged units only — no weight-based lines exist on this
      // model, so the qty detail is always the 'ea' form.
      lines.push(`  ${item.quantity} x $${item.unit_price.toFixed(2)}` + LF);
    }

    lines.push(LF);
    lines.push(this.dividerLine('-'));

    // Totals
    lines.push(COMMANDS.ALIGN_LEFT);
    lines.push(this.formatTotalLine('Subtotal:', receipt.subtotal));
    if (receipt.convenience_fee > 0) {
      lines.push(this.formatTotalLine('Convenience Fee:', receipt.convenience_fee));
    }
    lines.push(this.formatTotalLine('Tax:', receipt.tax));

    lines.push(this.dividerLine('-'));

    lines.push(COMMANDS.BOLD_ON);
    lines.push(COMMANDS.DOUBLE_HEIGHT_ON);
    lines.push(this.formatTotalLine('TOTAL:', receipt.total));
    lines.push(COMMANDS.NORMAL_SIZE);
    lines.push(COMMANDS.BOLD_OFF);
    lines.push(LF);

    // Substitution block. Prints ONLY when a line was swapped, so an
    // ordinary receipt is byte-identical to Build 1's.
    // The customer is handed this at the counter while cash changes hands,
    // so it must state what they were originally charged, what replaced
    // what, and which way the money went.
    if (receipt.substitutions && receipt.substitutions.length > 0) {
      lines.push(this.dividerLine('-'));
      lines.push(COMMANDS.BOLD_ON);
      lines.push('SUBSTITUTIONS' + LF);
      lines.push(COMMANDS.BOLD_OFF);
      for (const sub of receipt.substitutions) {
        lines.push(`Was: ${this.truncate(sub.original_name, PAPER_WIDTH - 6)}` + LF);
        lines.push(`     ${sub.original_quantity} @ $${sub.original_line_total.toFixed(2)}` + LF);
        lines.push(`Now: ${this.truncate(sub.substitute_name, PAPER_WIDTH - 6)}` + LF);
        lines.push(`     ${sub.substitute_quantity} @ $${sub.substitute_line_total.toFixed(2)}` + LF);
      }
      lines.push(LF);
      lines.push(this.formatTotalLine('Originally charged:', receipt.charged_total));
      if (receipt.refunded_cash > 0) {
        lines.push(this.formatTotalLine('CASH REFUNDED:', receipt.refunded_cash));
      }
      if (receipt.collected_cash > 0) {
        lines.push(this.formatTotalLine('CASH COLLECTED:', receipt.collected_cash));
      }
      if (receipt.waived_amount > 0) {
        // The customer owes nothing for this — say so plainly rather than
        // printing a charge they will not recognise.
        lines.push(this.formatTotalLine('Price increase waived:', receipt.waived_amount));
      }
      lines.push(LF);
    }

    // Payment. A BC order carries one settled payment on the order row
    // itself (no payments[] array), so this block is flat.
    lines.push(COMMANDS.BOLD_ON);
    lines.push('PAYMENT' + LF);
    lines.push(COMMANDS.BOLD_OFF);
    if (receipt.card_last_four) {
      lines.push(`${receipt.card_type || 'Card'} ****${receipt.card_last_four}` + LF);
    } else if (receipt.payment_method) {
      lines.push(`${receipt.payment_method}` + LF);
    }
    if (receipt.helcim_transaction_id) {
      lines.push(`Txn: ${receipt.helcim_transaction_id}` + LF);
    }
    lines.push(LF);

    // Footer — same as the POS receipt.
    lines.push(COMMANDS.ALIGN_CENTER);
    lines.push(this.dividerLine('='));
    lines.push(LF);
    lines.push('Thank you for your purchase!' + LF);
    lines.push(`${STORE_INFO.name1} ${STORE_INFO.name2}` + LF);
    lines.push(STORE_INFO.tagline + LF);
    lines.push(LF);

    lines.push(this.dividerLine('-'));
    lines.push(COMMANDS.BOLD_ON);
    lines.push('JOIN THE BRIDGE CLUB!' + LF);
    lines.push(COMMANDS.BOLD_OFF);
    lines.push('Earn rewards on every purchase' + LF);
    lines.push(LF);

    lines.push(COMMANDS.QR_MODEL);
    lines.push(COMMANDS.QR_SIZE(5));
    lines.push(COMMANDS.QR_ERROR);
    lines.push(COMMANDS.QR_STORE(STORE_INFO.bridgeClubUrl));
    lines.push(COMMANDS.QR_PRINT);
    lines.push(LF);

    lines.push('Scan to join or visit:' + LF);
    lines.push('bridgeclub.redbridgefeed.com' + LF);
    lines.push(this.dividerLine('-'));
    lines.push(LF);

    lines.push(COMMANDS.FEED_LINES(3));
    lines.push(COMMANDS.CUT_PARTIAL);
    // No drawer kick: nothing is tendered at the counter for a paid-online
    // order, so there is no cash to make change from.

    return [{
      type: 'raw',
      format: 'plain',
      data: lines.join(''),
    }];
  }

  /**
   * Helper: Create divider line
   */
  private dividerLine(char: string = '-'): string {
    return char.repeat(PAPER_WIDTH) + LF;
  }

  /**
   * Helper: Pad string to right
   */
  private padRight(str: string, length: number): string {
    return str + ' '.repeat(Math.max(0, length - str.length));
  }

  /**
   * Helper: Truncate string
   */
  private truncate(str: string, maxLength: number): string {
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength - 3) + '...';
  }

  /**
   * Helper: Format a total line with label and amount
   */
  private formatTotalLine(label: string, amount: number): string {
    const amountStr = (amount < 0 ? '-' : '') + '$' + Math.abs(amount).toFixed(2);
    return this.padRight(label, PAPER_WIDTH - amountStr.length) + amountStr + LF;
  }
}

// Singleton instance
export const receiptPrinter = new ReceiptPrinterService();

/**
 * Print a receipt (convenience function)
 */
export async function printReceipt(receipt: POSReceipt, openDrawer: boolean = false): Promise<boolean> {
  return receiptPrinter.printReceipt(receipt, openDrawer);
}

/**
 * Print a Bridge Club order receipt (convenience function)
 */
export async function printBCOrderReceipt(receipt: BCOrderReceipt): Promise<boolean> {
  return receiptPrinter.printBCOrderReceipt(receipt);
}

/**
 * Print a refund receipt (convenience function)
 */
export async function printRefundReceipt(receipt: POSRefundReceipt, openDrawer: boolean = false): Promise<boolean> {
  return receiptPrinter.printRefundReceipt(receipt, openDrawer);
}

/**
 * Print a till close report (convenience function)
 */
export async function printTillReport(data: TillCloseReport): Promise<boolean> {
  return receiptPrinter.printTillReport(data);
}

/**
 * Open cash drawer (convenience function)
 */
export async function openCashDrawer(): Promise<boolean> {
  return receiptPrinter.openDrawer();
}

/**
 * Find available printers
 */
export async function findPrinters(): Promise<string[]> {
  return receiptPrinter.findPrinters();
}

/**
 * Set the printer to use
 */
export function setPrinter(name: string): void {
  receiptPrinter.setPrinter(name);
}
